import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import {
  LayoutDashboard, FileText, Globe, Heart, MessageSquare,
  Star, LogOut, Menu, X, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/blogs', label: 'Blog Posts', icon: FileText },
  { path: '/admin/conferences', label: 'Conferences', icon: Globe },
  { path: '/admin/community', label: 'Community', icon: Heart },
  { path: '/admin/faqs', label: 'FAQs', icon: MessageSquare },
  { path: '/admin/media', label: 'Media & Press', icon: Star },
  { path: '/admin/gallery', label: 'Gallery', icon: Star },
  { path: '/admin/awards', label: 'Awards', icon: Star },
  { path: '/admin/messages', label: 'Messages', icon: MessageSquare },
  { path: '/admin/testimonials', label: 'Testimonials', icon: Star },
  { path: '/admin/newsletter', label: 'Newsletter', icon: Star },
];

const AdminLayout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await authAPI.logout();
    onLogout();
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-slate-800 transform transition-transform lg:translate-x-0 lg:static lg:inset-auto ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-5 border-b border-slate-800">
            <Link to="/" className="text-teal-400 font-bold text-lg" data-testid="admin-home-link">
              Dr. VPB Koyyala
            </Link>
            <p className="text-slate-500 text-xs mt-1">Admin Panel</p>
          </div>
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item)
                    ? 'bg-teal-700/20 text-teal-400'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-slate-800">
            <div className="text-sm text-slate-400 mb-3 truncate">{user?.email}</div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-500/10"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center gap-3 lg:hidden">
          <button onClick={() => setSidebarOpen(true)} className="text-slate-400 hover:text-white">
            <Menu className="w-6 h-6" />
          </button>
          <span className="text-white font-medium text-sm">Admin Panel</span>
        </header>
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
